import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, RefreshCw, Sparkles, AlertTriangle, Inbox, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Phase 55 — Admin-only dashboard:
 *   - Current-month + YTD + lifetime AI spend
 *   - Per-company budget controls (limit, extra quota, auto-parse toggle)
 *   - Pricing config (input/output $/M tokens per model)
 *   - Pending topup requests + approve/reject
 *
 * Rendered inside `AdminPanel.jsx` next to other admin cards.
 */
export default function AdminAICostDashboard() {
    const [summary, setSummary] = useState(null);
    const [budgets, setBudgets] = useState([]);
    const [pricing, setPricing] = useState(null);
    const [topups, setTopups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const refresh = async () => {
        setLoading(true);
        setError("");
        try {
            const [s, b, p, t] = await Promise.all([
                axios.get(`${API}/admin/ai-usage/summary`, { withCredentials: true }),
                axios.get(`${API}/admin/ai-budgets`, { withCredentials: true }),
                axios.get(`${API}/admin/ai-pricing`, { withCredentials: true }),
                axios.get(`${API}/admin/ai-topups?status=pending`, { withCredentials: true }),
            ]);
            setSummary(s.data);
            setBudgets(b.data.items || []);
            setPricing(p.data);
            setTopups(t.data.items || []);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, []);

    if (loading) return (
        <Card className="p-6 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading AI cost data…</Card>
    );
    if (error) return <Card className="p-6 text-rose-600 text-sm">{error}</Card>;

    return (
        <section data-testid="admin-ai-cost-section" className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="font-display font-bold text-2xl flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-ikb" /> AI cost & budget
                </h2>
                <Button size="sm" variant="outline" onClick={refresh} className="rounded-none" data-testid="ai-cost-refresh">
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
                </Button>
            </div>

            {/* Totals */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="ai-cost-totals">
                <Stat label={`Current month (${summary?.current_month})`} value={`$${(summary?.totals?.current_month_spent_usd ?? 0).toFixed(2)}`} />
                <Stat label="Year to date" value={`$${(summary?.totals?.ytd_spent_usd ?? 0).toFixed(2)}`} />
                <Stat label="All time" value={`$${(summary?.totals?.all_time_spent_usd ?? 0).toFixed(2)}`} />
                <Stat label="Active companies" value={(summary?.by_company || []).length} />
            </div>

            {/* By month sparkline-ish list */}
            <Card className="p-4">
                <h3 className="text-sm font-bold uppercase tracking-widest text-foreground/70 mb-3">Spend by month</h3>
                <div className="flex items-end gap-2 h-20" data-testid="ai-cost-by-month">
                    {(summary?.by_month || []).map((m) => {
                        const maxV = Math.max(0.01, ...(summary.by_month.map((x) => x.spent_usd)));
                        const h = Math.round((m.spent_usd / maxV) * 100);
                        return (
                            <div key={m.month} className="flex-1 flex flex-col items-center gap-1" title={`${m.month}: $${m.spent_usd}`}>
                                <div style={{ height: `${h}%`, minHeight: 2 }} className="w-full bg-ikb" />
                                <div className="text-[10px] font-mono text-foreground/60">{m.month.slice(5)}</div>
                                <div className="text-[10px] font-mono">${m.spent_usd.toFixed(2)}</div>
                            </div>
                        );
                    })}
                </div>
            </Card>

            {/* By operation */}
            <Card className="p-4">
                <h3 className="text-sm font-bold uppercase tracking-widest text-foreground/70 mb-3">Operations breakdown ({summary?.current_month})</h3>
                {(summary?.by_operation || []).length === 0 ? (
                    <div className="text-xs text-foreground/60">No tracked operations yet this month.</div>
                ) : (
                    <table className="w-full text-sm" data-testid="ai-cost-by-op">
                        <thead><tr className="text-[10px] font-mono uppercase text-foreground/60 border-b border-border">
                            <th className="text-left py-1.5">Operation</th><th className="text-right">Calls</th><th className="text-right">Spend (USD)</th>
                        </tr></thead>
                        <tbody>{(summary.by_operation || []).map((r) => (
                            <tr key={r.operation} className="border-b border-border/30">
                                <td className="py-1.5">{r.operation}</td>
                                <td className="text-right font-mono">{r.calls}</td>
                                <td className="text-right font-mono">${r.spent_usd.toFixed(4)}</td>
                            </tr>
                        ))}</tbody>
                    </table>
                )}
            </Card>

            {/* Per-company budgets */}
            <Card className="p-4">
                <h3 className="text-sm font-bold uppercase tracking-widest text-foreground/70 mb-3">Per-company budgets</h3>
                <CompanyBudgetTable budgets={budgets} onChange={refresh} />
            </Card>

            {/* Pricing config */}
            <Card className="p-4">
                <h3 className="text-sm font-bold uppercase tracking-widest text-foreground/70 mb-3 flex items-center justify-between">
                    <span>Token pricing (per 1M tokens)</span>
                    <Button size="sm" variant="outline" className="rounded-none" data-testid="ai-pricing-refresh-latest" onClick={async () => {
                        try { await axios.post(`${API}/admin/ai-pricing/refresh-latest`, {}, { withCredentials: true }); toast.success("Pricing snapped to latest defaults"); refresh(); }
                        catch (e) { toast.error(e?.response?.data?.detail || e.message); }
                    }}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Fetch latest</Button>
                </h3>
                <PricingTable pricing={pricing} onChange={refresh} />
            </Card>

            {/* Pending topups */}
            <Card className="p-4">
                <h3 className="text-sm font-bold uppercase tracking-widest text-foreground/70 mb-3 flex items-center gap-2">
                    <Inbox className="w-4 h-4" /> Pending topup requests {topups.length > 0 && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 font-mono">{topups.length}</span>}
                </h3>
                {topups.length === 0 ? (
                    <div className="text-xs text-foreground/60">No pending requests.</div>
                ) : (
                    <ul className="divide-y divide-border" data-testid="ai-topup-pending">
                        {topups.map((t) => (
                            <li key={t.id} className="py-2 flex items-center justify-between gap-3" data-testid={`ai-topup-${t.id}`}>
                                <div className="text-sm">
                                    <div className="font-bold">{t.company_name}</div>
                                    <div className="text-xs text-foreground/60 font-mono">${t.requested_amount_usd.toFixed(2)} — {t.note || "(no note)"}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button size="sm" className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`ai-topup-approve-${t.id}`} onClick={async () => {
                                        try { await axios.post(`${API}/admin/ai-topups/${t.id}/approve`, {}, { withCredentials: true }); toast.success("Topup approved"); refresh(); }
                                        catch (e) { toast.error(e?.response?.data?.detail || e.message); }
                                    }}><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                                    <Button size="sm" variant="outline" className="rounded-none" data-testid={`ai-topup-reject-${t.id}`} onClick={async () => {
                                        const reason = window.prompt("Reason for rejection?", "");
                                        try { await axios.post(`${API}/admin/ai-topups/${t.id}/reject`, { reason }, { withCredentials: true }); toast.success("Rejected"); refresh(); }
                                        catch (e) { toast.error(e?.response?.data?.detail || e.message); }
                                    }}><XCircle className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>
        </section>
    );
}

function Stat({ label, value }) {
    return (
        <div className="border border-border bg-white p-3 text-center">
            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/50">{label}</div>
            <div className="font-display font-black text-2xl mt-1">{value}</div>
        </div>
    );
}

function CompanyBudgetTable({ budgets, onChange }) {
    const [editing, setEditing] = useState({});  // company_id → patch
    const save = async (cid) => {
        const patch = editing[cid];
        if (!patch) return;
        try {
            await axios.put(`${API}/admin/ai-budgets/${cid}`, patch, { withCredentials: true });
            toast.success("Budget updated");
            setEditing((e) => { const n = { ...e }; delete n[cid]; return n; });
            onChange?.();
        } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    };
    if (budgets.length === 0) return <div className="text-xs text-foreground/60">No companies yet.</div>;
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ai-budgets-table">
                <thead><tr className="text-[10px] font-mono uppercase text-foreground/60 border-b border-border">
                    <th className="text-left py-1.5">Company</th>
                    <th className="text-right">Spent</th>
                    <th className="text-right">Limit ($/mo)</th>
                    <th className="text-right">+Extra ($)</th>
                    <th className="text-right">Remaining</th>
                    <th className="text-center">Auto-parse</th>
                    <th></th>
                </tr></thead>
                <tbody>{budgets.map((b) => {
                    const e = editing[b.company_id] || {};
                    const dirty = Object.keys(e).length > 0;
                    const limit = e.monthly_limit_usd ?? b.monthly_limit_usd;
                    const extraDelta = e.extra_quota_usd_delta ?? 0;
                    const auto = e.auto_ai_parsing_enabled ?? b.auto_ai_parsing_enabled;
                    return (
                        <tr key={b.company_id} className="border-b border-border/30" data-testid={`ai-budget-row-${b.company_id}`}>
                            <td className="py-1.5">{b.company_name || "(unknown)"}</td>
                            <td className="text-right font-mono">${b.spent_usd.toFixed(2)}</td>
                            <td className="text-right">
                                <Input type="number" value={limit} onChange={(ev) => setEditing((p) => ({ ...p, [b.company_id]: { ...(p[b.company_id] || {}), monthly_limit_usd: Number(ev.target.value) } }))} className="rounded-none h-7 text-xs w-24 text-right font-mono" data-testid={`ai-budget-limit-${b.company_id}`} />
                            </td>
                            <td className="text-right">
                                <Input type="number" placeholder="0" value={extraDelta} onChange={(ev) => setEditing((p) => ({ ...p, [b.company_id]: { ...(p[b.company_id] || {}), extra_quota_usd_delta: Number(ev.target.value) } }))} className="rounded-none h-7 text-xs w-20 text-right font-mono" data-testid={`ai-budget-extra-${b.company_id}`} />
                                <div className="text-[9px] font-mono text-foreground/40 mt-0.5">+ ${b.extra_quota_usd.toFixed(2)} now</div>
                            </td>
                            <td className={`text-right font-mono font-bold ${b.remaining_usd <= 0 ? "text-rose-600" : "text-emerald-700"}`}>${b.remaining_usd.toFixed(2)}</td>
                            <td className="text-center">
                                <input type="checkbox" checked={!!auto} onChange={(ev) => setEditing((p) => ({ ...p, [b.company_id]: { ...(p[b.company_id] || {}), auto_ai_parsing_enabled: ev.target.checked } }))} data-testid={`ai-budget-autoparse-${b.company_id}`} />
                            </td>
                            <td className="text-right">
                                {dirty && <Button size="sm" className="rounded-none bg-ikb hover:bg-ikb/90 text-white text-xs h-7 px-2" onClick={() => save(b.company_id)} data-testid={`ai-budget-save-${b.company_id}`}>Save</Button>}
                            </td>
                        </tr>
                    );
                })}</tbody>
            </table>
        </div>
    );
}

function PricingTable({ pricing, onChange }) {
    const [edits, setEdits] = useState({});
    if (!pricing) return null;
    const models = Object.keys(pricing.defaults).filter((m) => m !== "default");
    const overrideByModel = Object.fromEntries((pricing.overrides || []).map((o) => [o.model, o]));
    const save = async (model) => {
        const cur = edits[model] || {};
        const def = pricing.defaults[model];
        const ov = overrideByModel[model] || {};
        const body = {
            model,
            input_per_million: Number(cur.input_per_million ?? ov.input_per_million ?? def.input_per_million),
            output_per_million: Number(cur.output_per_million ?? ov.output_per_million ?? def.output_per_million),
        };
        try {
            await axios.put(`${API}/admin/ai-pricing`, body, { withCredentials: true });
            toast.success("Pricing updated");
            setEdits((p) => { const n = { ...p }; delete n[model]; return n; });
            onChange?.();
        } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    };
    return (
        <table className="w-full text-sm" data-testid="ai-pricing-table">
            <thead><tr className="text-[10px] font-mono uppercase text-foreground/60 border-b border-border">
                <th className="text-left py-1.5">Model</th>
                <th className="text-right">Input</th>
                <th className="text-right">Output</th>
                <th></th>
            </tr></thead>
            <tbody>{models.map((m) => {
                const ov = overrideByModel[m] || {};
                const def = pricing.defaults[m];
                const cur = edits[m] || {};
                const inp = cur.input_per_million ?? ov.input_per_million ?? def.input_per_million;
                const outp = cur.output_per_million ?? ov.output_per_million ?? def.output_per_million;
                const dirty = !!cur.input_per_million || !!cur.output_per_million;
                return (
                    <tr key={m} className="border-b border-border/30">
                        <td className="py-1.5 font-mono text-xs">{m}</td>
                        <td className="text-right"><Input type="number" step="0.01" value={inp} onChange={(ev) => setEdits((p) => ({ ...p, [m]: { ...(p[m] || {}), input_per_million: Number(ev.target.value) } }))} className="rounded-none h-7 text-xs w-20 text-right font-mono" data-testid={`ai-pricing-input-${m}`} /></td>
                        <td className="text-right"><Input type="number" step="0.01" value={outp} onChange={(ev) => setEdits((p) => ({ ...p, [m]: { ...(p[m] || {}), output_per_million: Number(ev.target.value) } }))} className="rounded-none h-7 text-xs w-20 text-right font-mono" data-testid={`ai-pricing-output-${m}`} /></td>
                        <td className="text-right">{dirty && <Button size="sm" className="rounded-none bg-ikb hover:bg-ikb/90 text-white text-xs h-7 px-2" onClick={() => save(m)} data-testid={`ai-pricing-save-${m}`}>Save</Button>}</td>
                    </tr>
                );
            })}</tbody>
        </table>
    );
}
